import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { NativeAuthLauncher } from '../plugins/nativeAuthLauncher';

const CAPACITOR_APP_ID = 'uk.co.inhouseapp.propertyhub';
const NATIVE_AUTH_INTENT_KEY = 'auth0:nativeAuthIntent';
const NATIVE_AUTH_STARTED_AT_KEY = 'auth0:nativeAuthStartedAt';
const NATIVE_AUTH_BROWSER_URL_KEY = 'auth0:nativeAuthBrowserUrl';
const NATIVE_LOGOUT_PENDING_AT_KEY = 'auth0:nativeLogoutPendingAt';
const NATIVE_QUEUED_AUTHORIZE_URL_KEY = 'auth0:nativeQueuedAuthorizeUrl';
const NATIVE_LAST_DEEP_LINK_URL_KEY = 'auth0:nativeLastDeepLinkUrl';
const NATIVE_LAST_DEEP_LINK_AT_KEY = 'auth0:nativeLastDeepLinkAt';
const NATIVE_DEEP_LINK_DEDUPE_WINDOW_MS = 3000;
const NATIVE_LOGOUT_PENDING_TTL_MS = 15000;

type NativeAuthIntent = 'login' | 'logout';
type NativeAuthSession = {
  intent: NativeAuthIntent;
  startedAt: number;
  browserUrl: string;
};

const sanitizeDomain = (domain: string): string =>
  domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');

const openNativeBrowserUrl = async (url: string) => {
  if (Capacitor.getPlatform() === 'android') {
    await NativeAuthLauncher.open({ url });
    return;
  }

  await Browser.open({ url, windowName: '_self' });
};

const getNativeCallbackUrl = (domain: string): string => {
  const auth0Domain = sanitizeDomain(domain);
  return `${CAPACITOR_APP_ID}://${auth0Domain}/capacitor/${CAPACITOR_APP_ID}/callback`;
};

const getNativeLogoutUrl = (domain: string): string => {
  const auth0Domain = sanitizeDomain(domain);
  return `${CAPACITOR_APP_ID}://${auth0Domain}/capacitor/${CAPACITOR_APP_ID}/logout`;
};

const getUrlPathname = (url: string): string | null => {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
};

export const describeNativeAuthUrlForLog = (url: string): string => {
  try {
    const parsed = new URL(url);
    const base = parsed.host
      ? `${parsed.protocol}//${parsed.host}${parsed.pathname}`
      : `${parsed.protocol}${parsed.pathname}`;
    const paramNames = Array.from(new Set(Array.from(parsed.searchParams.keys())));
    const querySummary =
      paramNames.length > 0 ? `?${paramNames.map((name) => `${name}=[redacted]`).join('&')}` : '';
    const hashSummary = parsed.hash ? '#[redacted]' : '';

    return `${base}${querySummary}${hashSummary}`;
  } catch {
    return '[invalid URL]';
  }
};

const isNativeAuthorizeUrl = (url: string): boolean => getUrlPathname(url) === '/authorize';
const isNativeLogoutBrowserUrl = (url: string): boolean => getUrlPathname(url) === '/v2/logout';

const getPendingLogoutStartedAt = (): number | null => {
  const value = window.localStorage.getItem(NATIVE_LOGOUT_PENDING_AT_KEY);

  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clearNativeAuthSession = () => {
  window.localStorage.removeItem(NATIVE_AUTH_INTENT_KEY);
  window.localStorage.removeItem(NATIVE_AUTH_STARTED_AT_KEY);
  window.localStorage.removeItem(NATIVE_AUTH_BROWSER_URL_KEY);
};

const setNativeAuthSession = (intent: NativeAuthIntent, url: string) => {
  window.localStorage.setItem(NATIVE_AUTH_INTENT_KEY, intent);
  window.localStorage.setItem(NATIVE_AUTH_STARTED_AT_KEY, Date.now().toString());
  window.localStorage.setItem(NATIVE_AUTH_BROWSER_URL_KEY, url);
};

const clearNativeLogoutState = () => {
  window.localStorage.removeItem(NATIVE_LOGOUT_PENDING_AT_KEY);
  window.localStorage.removeItem(NATIVE_QUEUED_AUTHORIZE_URL_KEY);
};

const hasFreshNativeLogoutPending = (): boolean => {
  const startedAt = getPendingLogoutStartedAt();

  if (startedAt === null) {
    return false;
  }

  return Date.now() - startedAt < NATIVE_LOGOUT_PENDING_TTL_MS;
};

export const clearStaleNativeLogoutState = () => {
  const startedAt = getPendingLogoutStartedAt();

  if (startedAt === null) {
    return;
  }

  if (Date.now() - startedAt < NATIVE_LOGOUT_PENDING_TTL_MS) {
    return;
  }

  console.log('[auth0] clearing stale native logout state');
  clearNativeLogoutState();
};

export const getNativeAuthSession = (): NativeAuthSession | null => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  const intent = window.localStorage.getItem(NATIVE_AUTH_INTENT_KEY);
  const startedAt = Number(window.localStorage.getItem(NATIVE_AUTH_STARTED_AT_KEY));
  const browserUrl = window.localStorage.getItem(NATIVE_AUTH_BROWSER_URL_KEY);

  if ((intent !== 'login' && intent !== 'logout') || !Number.isFinite(startedAt) || !browserUrl) {
    return null;
  }

  return {
    intent,
    startedAt,
    browserUrl,
  };
};

export const markNativeDeepLinkHandled = (url: string): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const lastUrl = window.localStorage.getItem(NATIVE_LAST_DEEP_LINK_URL_KEY);
  const lastHandledAt = Number(window.localStorage.getItem(NATIVE_LAST_DEEP_LINK_AT_KEY));
  const now = Date.now();

  if (lastUrl === url && Number.isFinite(lastHandledAt) && now - lastHandledAt < NATIVE_DEEP_LINK_DEDUPE_WINDOW_MS) {
    // REQ-048: native auth URLs can carry code/state values and must be redacted in logs.
    console.log('[auth0] ignoring duplicate native deep link', describeNativeAuthUrlForLog(url));
    return true;
  }

  window.localStorage.setItem(NATIVE_LAST_DEEP_LINK_URL_KEY, url);
  window.localStorage.setItem(NATIVE_LAST_DEEP_LINK_AT_KEY, now.toString());
  return false;
};

export const completeNativeLogin = () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  console.log('[auth0] marking native login complete');
  clearNativeAuthSession();
};

export const completeNativeLogout = () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  console.log('[auth0] marking native logout complete');
  clearNativeAuthSession();
  window.localStorage.removeItem(NATIVE_LOGOUT_PENDING_AT_KEY);
};

export const consumeQueuedNativeAuthorizeUrl = (): string | null => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  const queuedUrl = window.localStorage.getItem(NATIVE_QUEUED_AUTHORIZE_URL_KEY);
  window.localStorage.removeItem(NATIVE_QUEUED_AUTHORIZE_URL_KEY);
  return queuedUrl;
};

export const getAuth0RedirectUri = (domain: string): string => {
  if (Capacitor.isNativePlatform()) {
    return getNativeCallbackUrl(domain);
  }

  return `${window.location.origin}/dashboard`;
};

export const getAuth0LogoutReturnTo = (domain: string, webPath = ''): string => {
  if (Capacitor.isNativePlatform()) {
    return getNativeLogoutUrl(domain);
  }

  return `${window.location.origin}${webPath}`;
};

export const openNativeAuth0Url = async (url: string) => {
  clearStaleNativeLogoutState();

  if (isNativeLogoutBrowserUrl(url)) {
    // REQ-048: native auth URLs can carry state-like parameters and must be redacted in logs.
    console.log('[auth0] launching native logout browser URL', describeNativeAuthUrlForLog(url));
    setNativeAuthSession('logout', url);
    window.localStorage.setItem(NATIVE_LOGOUT_PENDING_AT_KEY, Date.now().toString());
    window.localStorage.removeItem(NATIVE_QUEUED_AUTHORIZE_URL_KEY);
    await openNativeBrowserUrl(url);
    return;
  }

  if (isNativeAuthorizeUrl(url) && hasFreshNativeLogoutPending()) {
    // REQ-048: native authorize URLs include Auth0 transaction parameters.
    console.log('[auth0] deferring native authorize URL until logout completes', describeNativeAuthUrlForLog(url));
    window.localStorage.setItem(NATIVE_QUEUED_AUTHORIZE_URL_KEY, url);
    return;
  }

  if (isNativeAuthorizeUrl(url)) {
    // REQ-048: native authorize URLs include Auth0 transaction parameters.
    console.log('[auth0] launching native login browser URL', describeNativeAuthUrlForLog(url));
    setNativeAuthSession('login', url);
  }

  await openNativeBrowserUrl(url);
};

export const getAuth0OpenUrlOption = () => {
  if (!Capacitor.isNativePlatform()) {
    return {};
  }

  return {
    openUrl: async (url: string) => openNativeAuth0Url(url),
  };
};

export const getAuth0SignupAuthorizationParams = () => {
  if (!Capacitor.isNativePlatform()) {
    return {};
  }

  return {
    prompt: 'login' as const,
  };
};

export const getAuth0LoginAuthorizationParams = () => {
  if (!Capacitor.isNativePlatform()) {
    return {};
  }

  return {
    prompt: 'login' as const,
  };
};

export const isNativeAuth0CallbackUrl = (url: string, domain: string): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  return url.startsWith(getNativeCallbackUrl(domain));
};

export const isNativeAuth0LogoutUrl = (url: string, domain: string): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  return url.startsWith(getNativeLogoutUrl(domain));
};
